import { Button, VStack } from '@chakra-ui/react'
import { AxiosError } from 'axios'
import { FormEvent, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  EMPTY_EVALUATED_EMPLOYEE,
  EvaluatedEmployeeFields,
  evaluatedEmployeeErrors,
  evaluatedEmployeePayload,
  IEvaluatedEmployeeForm,
  TEvaluatedEmployeeErrors,
  withoutError,
  withoutErrorsOf
} from '../../../components/EvaluatedEmployeeFields'
import { FormField } from '../../../components/FormField'
import PublicFormLayout from '../../../components/publicFormLayout/PublicFormLayout'
import { FormSelect } from '../../../components/Select'
import { useAppToast } from '../../../helpers'
import ConexaoMagentaService from '../../../services/ConexaoMagenta/ConexaoMagentaService'
import { IUser, UserService } from '../../../services/User/UserService'

type ApiError = AxiosError<{ errors?: TEvaluatedEmployeeErrors }>

// Acelera and Conexão also ask the nickname and the birthdate.
const ASKS_PERSONAL = true

export default function ConexaoMagentaNewPage() {
  const toast = useAppToast()
  const navigate = useNavigate()
  const [employeeForm, setEmployeeForm] = useState<IEvaluatedEmployeeForm>(EMPTY_EVALUATED_EMPLOYEE)
  const [managerId, setManagerId] = useState('')
  const [managers, setManagers] = useState<IUser[]>([])
  const [errors, setErrors] = useState<TEvaluatedEmployeeErrors>({})
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    new UserService()
      .getManagers()
      .then(setManagers)
      .catch((error) => toast.error('Erro ao carregar os gestores', error))
  }, [])

  const updateEmployee = (patch: Partial<IEvaluatedEmployeeForm>) => {
    setEmployeeForm((current) => ({ ...current, ...patch }))
    setErrors((current) => withoutErrorsOf(current, patch))
  }

  const dropError = (key: string) => setErrors((current) => withoutError(current, key))

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const formErrors = evaluatedEmployeeErrors(employeeForm, ASKS_PERSONAL)
    if (!managerId) formErrors.manager_id = ['Selecione o(a) gestor(a) imediato(a).']
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors)
      toast.invalidForm()
      return
    }

    setSaving(true)
    try {
      const response = await new ConexaoMagentaService().store({
        ...evaluatedEmployeePayload(employeeForm, ASKS_PERSONAL),
        manager_id: Number(managerId)
      })
      navigate(`/conexao-magenta/${response.data.id}`)
    } catch (error) {
      const apiErrors = (error as ApiError).response?.data?.errors
      if (apiErrors) {
        setErrors(apiErrors)
        toast.invalidForm()
        return
      }
      toast.error('Erro ao iniciar o Conexão Magenta', error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <PublicFormLayout title="Conexão Magenta">
      <form onSubmit={handleSubmit} noValidate>
        <VStack spacing="4">
          <EvaluatedEmployeeFields
            value={employeeForm}
            onChange={updateEmployee}
            errors={errors}
            asksPersonal={ASKS_PERSONAL}
          />
          <FormField label="Gestor(a) imediato(a)" error={errors.manager_id} isRequired>
            <FormSelect
              value={managerId}
              onChange={(value) => {
                setManagerId(value)
                dropError('manager_id')
              }}
              options={managers.map((manager) => ({ value: manager.id, label: manager.name }))}
              placeholder="Selecione um gestor"
            />
          </FormField>
          <Button colorScheme="magenta" type="submit" isLoading={saving}>
            Iniciar Conexão Magenta
          </Button>
        </VStack>
      </form>
    </PublicFormLayout>
  )
}
